import { type FormEvent, useState } from 'react';
import { Button, Card, Input } from '@/shared/components';
import { ApiError } from '@/shared/api';

export interface LoginFormValues {
  userNameOrEmailAddress: string;
  password: string;
  rememberMe: boolean;
}

interface LoginFormProps {
  onSubmit: (values: LoginFormValues) => Promise<void>;
  isLoading?: boolean;
  error?: Error | null;
}

export function LoginForm({ onSubmit, isLoading = false, error }: LoginFormProps) {
  const [userNameOrEmailAddress, setUserNameOrEmailAddress] = useState('');
  const [password, setPassword] = useState('');
  const [rememberMe, setRememberMe] = useState(false);

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    await onSubmit({
      userNameOrEmailAddress,
      password,
      rememberMe,
    });
  };

  const errorMessage = error
    ? (error instanceof ApiError ? ApiError.getUserMessage(error) : error.message)
    : null;

  return (
    <Card className="w-full max-w-md p-8">
      <form onSubmit={handleSubmit} className="flex flex-col gap-5">
        <Input
          label="Username or email"
          type="text"
          autoComplete="username"
          value={userNameOrEmailAddress}
          onChange={(e) => setUserNameOrEmailAddress(e.target.value)}
          placeholder="Enter your username or email"
          required
          disabled={isLoading}
        />
        <Input
          label="Password"
          type="password"
          autoComplete="current-password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          placeholder="Enter your password"
          required
          disabled={isLoading}
        />
        <label className="flex cursor-pointer items-center gap-2">
          <input
            type="checkbox"
            checked={rememberMe}
            onChange={(e) => setRememberMe(e.target.checked)}
            disabled={isLoading}
            className="h-4 w-4 rounded border-slate-300 text-indigo-600 focus:ring-indigo-500"
          />
          <span className="text-sm text-slate-700">Remember me</span>
        </label>
        {errorMessage && (
          <div
            role="alert"
            className="rounded-lg border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-700"
          >
            {errorMessage}
          </div>
        )}
        <Button
          type="submit"
          disabled={isLoading}
          className="w-full"
        >
          {isLoading ? 'Signing in…' : 'Sign in'}
        </Button>
      </form>
    </Card>
  );
}
